import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Logo, Wordmark } from "@/components/Logo";
import { SignOut, Buildings, Users, ClipboardText, TrendUp, EyeSlash, Eye, Trash, ArrowsClockwise, LinkSimple, House, EnvelopeSimple, Shield } from "@phosphor-icons/react";

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [platform, setPlatform] = useState(null);
  const [labelsEnabled, setLabelsEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: "", plan: "free" });
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const [t, s, l, p, k] = await Promise.all([
        api.get("/super-admin/tenants"),
        api.get("/super-admin/stats"),
        api.get("/leads").catch(() => ({ data: [] })),
        api.get("/super-admin/platform-settings").catch(() => ({ data: null })),
        api.get("/emergency-map/kill-switch/labels").catch(() => ({ data: { enabled: true } })),
      ]);
      setTenants(t.data);
      setStats(s.data);
      setLeads(l.data);
      setPlatform(p.data);
      setLabelsEnabled(!!k.data?.enabled);
    } catch (e) { toast.error("Failed to load"); }
  };

  const toggleEmergencyLabels = async () => {
    const turningOff = labelsEnabled;
    if (turningOff && !window.confirm("Kill the Emergency Label PDF generator for ALL tenants? Any tenant trying to print sticker sheets will get a 403 until re-enabled.")) return;
    try {
      const { data } = await api.post("/emergency-map/kill-switch/labels", { enabled: !labelsEnabled });
      setLabelsEnabled(!!data.enabled);
      toast.success(data.enabled ? "Emergency labels re-enabled" : "Emergency labels killed globally");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  useEffect(() => { load(); }, []);

  const toggleAI = async (t) => {
    try {
      await api.patch(`/super-admin/tenants/${t.id}`, { ai_enabled: !t.ai_toggle });
      toast.success(`AI ${!t.ai_toggle ? "enabled" : "disabled"} for ${t.name}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const togglePortal = async (t) => {
    try {
      await api.post(`/super-admin/tenants/${t.id}/toggle-client-portal`, { enabled: !t.client_portal_enabled });
      toast.success(`Client Portal ${!t.client_portal_enabled ? "ON" : "OFF"} for ${t.name}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const togglePortalEmails = async (t) => {
    const turningOn = !t.client_portal_emails_enabled;
    if (turningOn && !window.confirm(`⚠️  ENABLE portal invite EMAILS for ${t.name}?\n\nThis will let their admins send real Resend invites to landlords + PMR. Confirm they have authority to email these people.`)) return;
    try {
      await api.post(`/super-admin/tenants/${t.id}/toggle-portal-emails`, { enabled: turningOn });
      toast.success(`Portal invite emails ${turningOn ? "ENABLED" : "DISABLED"} for ${t.name}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleGlobalPortal = async () => {
    const disabling = !platform?.client_portal_globally_disabled;
    if (disabling && !window.confirm("Kill the Client Portal for ALL tenants right now? Every landlord/PMR portal link will return 503 until re-enabled.")) return;
    try {
      const { data } = await api.post("/super-admin/platform/toggle-portal-globally", { disabled: disabling });
      setPlatform(data);
      toast.success(disabling ? "Portal killed globally" : "Portal re-enabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const impersonate = (tenant) => {
    // Store impersonation flag and navigate to admin
    localStorage.setItem("impersonate_tenant", tenant.id);
    localStorage.setItem("impersonate_tenant_name", tenant.name);
    toast.info(`Now viewing as ${tenant.name}`);
    nav("/admin");
  };

  const changePlan = async (t, plan) => {
    try {
      await api.patch(`/super-admin/tenants/${t.id}`, { plan });
      toast.success(`${t.name} → ${plan.toUpperCase()}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleStatus = async (t) => {
    const newStatus = t.status === "active" ? "suspended" : "active";
    if (!window.confirm(`${newStatus === "suspended" ? "SUSPEND" : "REACTIVATE"} ${t.name}? Users will ${newStatus === "suspended" ? "be locked out" : "regain access"}.`)) return;
    try {
      await api.patch(`/super-admin/tenants/${t.id}`, { status: newStatus });
      toast.success(`${t.name} is now ${newStatus}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteTenant = async (t) => {
    if (!window.confirm(`DELETE ${t.name}? This wipes ALL their data. Type the company name to confirm:`)) return;
    const confirm = window.prompt(`Type "${t.name}" to confirm irreversible deletion:`);
    if (confirm !== t.name) { toast.error("Name mismatch — cancelled"); return; }
    try {
      await api.delete(`/super-admin/tenants/${t.id}`);
      toast.success(`${t.name} deleted`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createTenant = async (e) => {
    e.preventDefault();
    if (!newTenant.name.trim()) return;
    // Basic validation for optional admin seed
    if ((newTenant.admin_email || newTenant.admin_password) && !(newTenant.admin_email && newTenant.admin_password && newTenant.admin_password.length >= 6)) {
      toast.error("If seeding an admin, provide BOTH email and a password of at least 6 chars");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: newTenant.name.trim(), plan: newTenant.plan };
      if (newTenant.admin_email && newTenant.admin_password) {
        payload.admin_email = newTenant.admin_email.trim().toLowerCase();
        payload.admin_password = newTenant.admin_password;
        payload.admin_name = (newTenant.admin_name || "Admin").trim();
      }
      const { data } = await api.post("/super-admin/tenants", payload);
      toast.success(data.admin_seeded ? `Created ${newTenant.name} with admin login` : `Created ${newTenant.name} (no admin yet)`);
      setNewTenant({ name: "", plan: "free" });
      setShowCreate(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#f4f4f5]">
      <header className="sticky top-0 z-30 bg-[#0f172a] text-white border-b-4 border-[#00C4CC]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#00C4CC] flex items-center justify-center border-2 border-white">
              <Logo size={26}/>
            </div>
            <div>
              <Wordmark dark size="text-lg"/>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">Platform Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-xs uppercase text-slate-400 font-bold">God Account</div>
              <div className="text-sm font-bold">{user?.name}</div>
            </div>
            <button onClick={logout} data-testid="super-logout-btn" className="h-10 px-3 bg-slate-800 hover:bg-slate-700 flex items-center gap-2 text-sm font-bold uppercase">
              <SignOut weight="bold" size={16}/> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tight">Platform Overview</h1>
          <p className="mt-1 text-slate-500 font-medium">Every company running on Field Radar.</p>
        </div>

        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={<Buildings weight="bold" size={22}/>} label="Companies" value={stats.tenants} sub={`${stats.active_tenants} active · ${stats.suspended_tenants} suspended`}/>
            <StatCard icon={<Users weight="bold" size={22}/>} label="Total Users" value={stats.total_users}/>
            <StatCard icon={<ClipboardText weight="bold" size={22}/>} label="Total Jobs" value={stats.total_jobs}/>
            <StatCard icon={<TrendUp weight="bold" size={22}/>} label="Sign-up Leads" value={stats.total_leads}/>
          </div>
        )}

        {/* Global kill switches */}
        <section className="rugged-card p-6 border-red-500 border-l-4" data-testid="super-kill-switches">
          <div className="flex items-center gap-2">
            <Shield weight="fill" size={20} className="text-red-600"/>
            <h2 className="font-display font-black text-xl uppercase">Platform Kill Switches</h2>
          </div>
          <p className="mt-1 text-sm text-slate-600 font-medium">One click, everyone off. Use in emergencies or before you're ready to launch a feature.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`p-4 border-2 ${platform?.client_portal_globally_disabled ? "bg-red-50 border-red-500" : "bg-white border-[#0f172a]"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-black uppercase text-sm tracking-widest">Client Portal · Global</div>
                  <p className="mt-1 text-xs text-slate-600">Kill every landlord/PMR portal across every tenant instantly.</p>
                </div>
                <button onClick={toggleGlobalPortal} data-testid="global-portal-toggle"
                  className={`px-3 py-1.5 font-black uppercase text-xs tracking-widest border-2 ${platform?.client_portal_globally_disabled ? "bg-red-600 text-white border-red-700" : "bg-green-500 text-white border-green-600"}`}>
                  {platform?.client_portal_globally_disabled ? "GLOBALLY OFF" : "GLOBALLY ON"}
                </button>
              </div>
            </div>
            <div className={`p-4 border-2 ${labelsEnabled ? "bg-white border-[#0f172a]" : "bg-red-50 border-red-500"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-black uppercase text-sm tracking-widest">Emergency Labels · Global</div>
                  <p className="mt-1 text-xs text-slate-600">Physical valve sticker PDFs (Avery 5163). Kill this if a company abuses print or a design bug slips.</p>
                </div>
                <button onClick={toggleEmergencyLabels} data-testid="emergency-labels-toggle"
                  className={`px-3 py-1.5 font-black uppercase text-xs tracking-widest border-2 ${labelsEnabled ? "bg-green-500 text-white border-green-600" : "bg-red-600 text-white border-red-700"}`}>
                  {labelsEnabled ? "GLOBALLY ON" : "GLOBALLY OFF"}
                </button>
              </div>
            </div>
            <div className="p-4 border-2 bg-slate-50 border-slate-300 sm:col-span-2">
              <div className="font-black uppercase text-sm tracking-widest text-slate-700">Per-Company Controls</div>
              <p className="mt-1 text-xs text-slate-600">
                Use the "Portal" and "✉" buttons in the Companies table to toggle portal + invite emails per company.
                Emails stay OFF until you flip them — no accidental sends.
              </p>
            </div>
          </div>
        </section>

        {/* Data migration (Preview ↔ Production) */}
        <DataMigrationSection tenants={tenants}/>



        {/* Tenants section */}
        <section className="rugged-card p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display font-black text-2xl uppercase">Companies</h2>
            <button onClick={() => setShowCreate(true)} data-testid="super-create-tenant-btn"
              className="tactile-btn h-10 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider">
              + Create Company
            </button>
          </div>

          {showCreate && (
            <form onSubmit={createTenant} className="mt-4 p-4 bg-slate-50 border-2 border-[#0f172a] space-y-2" data-testid="super-create-form">
              <div className="flex flex-wrap gap-2">
                <input required value={newTenant.name} onChange={e => setNewTenant({ ...newTenant, name: e.target.value })}
                  placeholder="Company name *" data-testid="super-create-name"
                  className="flex-1 min-w-[220px] h-10 px-3 border-2 border-[#0f172a] font-medium"/>
                <select value={newTenant.plan} onChange={e => setNewTenant({ ...newTenant, plan: e.target.value })}
                  data-testid="super-create-plan"
                  className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs bg-white">
                  <option value="free">Free (1 worker)</option>
                  <option value="pro">Pro (Unlimited)</option>
                </select>
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-1">Seed the first admin (optional — leave blank to create later)</div>
              <div className="flex flex-wrap gap-2">
                <input value={newTenant.admin_name || ""} onChange={e => setNewTenant({ ...newTenant, admin_name: e.target.value })}
                  placeholder="Admin name" data-testid="super-create-admin-name"
                  className="flex-1 min-w-[160px] h-10 px-3 border-2 border-[#0f172a] font-medium"/>
                <input type="email" value={newTenant.admin_email || ""} onChange={e => setNewTenant({ ...newTenant, admin_email: e.target.value })}
                  placeholder="admin@company.com" data-testid="super-create-admin-email"
                  className="flex-1 min-w-[220px] h-10 px-3 border-2 border-[#0f172a] font-medium"/>
                <input type="password" value={newTenant.admin_password || ""} onChange={e => setNewTenant({ ...newTenant, admin_password: e.target.value })}
                  placeholder="Password (min 6)" data-testid="super-create-admin-password"
                  className="w-[180px] h-10 px-3 border-2 border-[#0f172a] font-medium"/>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={busy}
                  className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider">
                  {busy ? "Creating…" : "Create"}
                </button>
                <button type="button" onClick={() => setShowCreate(false)}
                  className="h-10 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white">
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="mt-4 overflow-x-auto" data-testid="super-tenants-list">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-[#0f172a] text-white text-xs font-black uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-center">AI · Portal · Emails</th>
                  <th className="px-3 py-2 text-right">Admins</th>
                  <th className="px-3 py-2 text-right">Workers</th>
                  <th className="px-3 py-2 text-right">Jobs</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`tenant-row-${t.id}`}>
                    <td className="px-3 py-3">
                      <div className="font-black">{t.name}</div>
                      <div className="text-xs font-mono text-slate-500">{t.slug}</div>
                    </td>
                    <td className="px-3 py-3">
                      <select value={t.plan} onChange={e => changePlan(t, e.target.value)}
                  data-testid={`tenant-plan-${t.id}`}
                  className="h-8 px-2 border-2 border-[#0f172a] text-xs font-black uppercase bg-white">
                  <option value="trial">Trial</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                </select>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-1 text-xs font-black uppercase ${t.status === "active" ? "bg-green-100 text-green-700 border-2 border-green-500" : "bg-red-100 text-red-700 border-2 border-red-500"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => toggleAI(t)} data-testid={`tenant-ai-${t.id}`}
                        className={`px-2 py-1 text-xs font-black uppercase border-2 ${t.ai_enabled ? "bg-violet-100 text-violet-800 border-violet-500" : "bg-slate-100 text-slate-600 border-slate-400"}`}>
                        AI {t.ai_enabled ? "ON" : "OFF"}
                      </button>
                      <div className="mt-1 flex gap-1">
                        <button onClick={() => togglePortal(t)} data-testid={`tenant-portal-${t.id}`}
                          title="Master switch: allows landlord/PMR portal logins"
                          className={`px-2 py-0.5 text-[10px] font-black uppercase border-2 ${t.client_portal_enabled ? "bg-cyan-100 text-cyan-800 border-cyan-500" : "bg-slate-100 text-slate-600 border-slate-400"}`}>
                          Portal {t.client_portal_enabled ? "ON" : "OFF"}
                        </button>
                        <button onClick={() => togglePortalEmails(t)} data-testid={`tenant-portal-emails-${t.id}`}
                          title="Independent switch: allows landlord/PMR invite EMAILS to fire"
                          className={`px-2 py-0.5 text-[10px] font-black uppercase border-2 ${t.client_portal_emails_enabled ? "bg-orange-100 text-orange-800 border-orange-500" : "bg-slate-100 text-slate-600 border-slate-400"}`}>
                          ✉ {t.client_portal_emails_enabled ? "ON" : "OFF"}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-bold">{t.admin_count}</td>
                    <td className="px-3 py-3 text-right font-bold">{t.worker_count}</td>
                    <td className="px-3 py-3 text-right font-bold">{t.job_count}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button onClick={() => impersonate(t)} title="View as this company" data-testid={`tenant-impersonate-${t.id}`}
                        className="text-xs font-black uppercase tracking-wider text-[#00C4CC] hover:underline mr-3 inline-flex items-center gap-1">
                        <LinkSimple weight="bold" size={12}/> Enter
                      </button>
                      <button onClick={() => toggleStatus(t)} title={t.status === "active" ? "Suspend" : "Reactivate"}
                        data-testid={`tenant-toggle-${t.id}`}
                        className={`text-xs font-black uppercase tracking-wider hover:underline mr-3 ${t.status === "active" ? "text-amber-600" : "text-green-600"}`}>
                        {t.status === "active" ? <><EyeSlash weight="bold" size={12} className="inline mr-1"/>Suspend</> : <><Eye weight="bold" size={12} className="inline mr-1"/>Activate</>}
                      </button>
                      <button onClick={() => deleteTenant(t)} title="Delete tenant" data-testid={`tenant-delete-${t.id}`}
                        className="text-xs font-black uppercase tracking-wider text-red-600 hover:underline inline-flex items-center gap-1">
                        <Trash weight="bold" size={12}/> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400 font-medium">No companies yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Leads */}
        <section className="rugged-card p-6">
          <h2 className="font-display font-black text-2xl uppercase">Book-a-Demo Leads ({leads.length})</h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">Prospects who filled the public landing form.</p>
          <div className="mt-4 overflow-x-auto" data-testid="super-leads-list">
            {leads.length === 0 ? (
              <div className="text-sm text-slate-400 font-medium py-4 text-center border-2 border-dashed border-slate-300">No leads yet.</div>
            ) : (
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-[#0f172a] text-white text-xs font-black uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Company</th>
                    <th className="px-3 py-2 text-left">Contact</th>
                    <th className="px-3 py-2 text-left">Email / Phone</th>
                    <th className="px-3 py-2 text-left">Crew</th>
                    <th className="px-3 py-2 text-left">Message</th>
                    <th className="px-3 py-2 text-right">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id} className="border-t border-slate-200">
                      <td className="px-3 py-2 font-bold">{l.company}</td>
                      <td className="px-3 py-2">{l.contact_name}</td>
                      <td className="px-3 py-2">
                        <a href={`mailto:${l.email}`} className="text-[#00C4CC] hover:underline block">{l.email}</a>
                        {l.phone && <span className="text-xs text-slate-500">{l.phone}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">{l.crew_size || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate">{l.message || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 text-right">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function DataMigrationSection({ tenants }) {
  const [sourceTid, setSourceTid] = useState("");
  const [targetTid, setTargetTid] = useState("");
  const [kind, setKind] = useState("inspection_projects"); // or "tenant_inspections"
  const [photoQuality, setPhotoQuality] = useState("small");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [listBusy, setListBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  const listPath = kind === "inspection_projects"
    ? "/super-admin/list/inspection-projects"
    : kind === "tenant_inspections"
    ? "/super-admin/list/tenant-inspections"
    : kind === "landlords"
    ? "/super-admin/list/landlords"
    : "/super-admin/list/assets";
  const exportPath = kind === "inspection_projects"
    ? "/super-admin/export/inspection-projects"
    : kind === "tenant_inspections"
    ? "/super-admin/export/tenant-inspections"
    : kind === "landlords"
    ? "/super-admin/export/landlords"
    : "/super-admin/export/assets";
  const hasPhotos = kind === "inspection_projects" || kind === "tenant_inspections";

  // Load list when source or kind changes
  useEffect(() => {
    if (!sourceTid) { setRows([]); setSelected(new Set()); return; }
    setListBusy(true); setRows([]); setSelected(new Set());
    api.get(`${listPath}?tenant_id=${sourceTid}`)
      .then(r => {
        setRows(r.data.items || []);
        setSelected(new Set((r.data.items || []).map(x => x.id))); // select-all by default
      })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to list"))
      .finally(() => setListBusy(false));
  }, [sourceTid, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  };

  const selectedPhotoCount = rows.filter(r => selected.has(r.id)).reduce((a, r) => a + (r.photo_count || 0), 0);
  const estMB = !hasPhotos
    ? Math.max(1, Math.round(selected.size * 0.005))   // ~5KB / landlord+links
    : photoQuality === "small"
    ? Math.max(1, Math.round(selectedPhotoCount * 0.45))
    : Math.max(1, Math.round(selectedPhotoCount * 4.5));

  const doExport = async () => {
    if (!sourceTid) { toast.error("Pick a source company"); return; }
    if (selected.size === 0) { toast.error("Select at least one item"); return; }
    setExportBusy(true);
    try {
      const idsParam = Array.from(selected).join(",");
      const { data } = await api.get(`${exportPath}?tenant_id=${sourceTid}&ids=${idsParam}&photo_quality=${photoQuality}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const src = tenants.find(t => t.id === sourceTid);
      const label = kind === "inspection_projects" ? "projects" : "inspections";
      a.download = `field-radar-${label}-${(src?.name || sourceTid).replace(/[^a-z0-9]/gi, "_")}-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const count = data.project_count ?? data.inspection_count ?? 0;
      toast.success(`Exported ${count} ${label} · ${data.photo_count} photo(s) · ${photoQuality}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    } finally { setExportBusy(false); }
  };

  const doImport = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    if (!targetTid) { toast.error("Pick a target company first"); return; }
    setImportBusy(true);
    setImportSummary(null);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const schema = payload.schema || "";
      let path;
      if (schema.includes("inspection_projects")) path = "/super-admin/import/inspection-projects";
      else if (schema.includes("tenant_inspections")) path = "/super-admin/import/tenant-inspections";
      else if (schema.includes("landlords")) path = "/super-admin/import/landlords";
      else if (schema.includes("assets")) path = "/super-admin/import/assets";
      else throw new Error("Unknown schema in upload — not a Field Radar export.");
      const { data } = await api.post(path, {
        target_tenant_id: targetTid,
        payload,
        skip_existing_names: true,
        skip_existing: true,
      });
      setImportSummary({ ...data, schema });
      toast.success(`Imported ${data.imported} · skipped ${data.skipped_existing} · ${data.photos_migrated} photo(s)`);
    } catch (e) {
      if (e?.message?.includes("JSON") || e?.message?.includes("schema"))
        toast.error(e.message);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setImportBusy(false); }
  };

  return (
    <section className="rugged-card p-6 border-l-4 border-fuchsia-500" data-testid="super-migration">
      <div className="flex items-center gap-2">
        <ArrowsClockwise weight="fill" size={20} className="text-fuchsia-600"/>
        <h2 className="font-display font-black text-xl uppercase">Data Migration — Preview ↔ Production</h2>
      </div>
      <p className="mt-1 text-sm text-slate-600 font-medium">
        Pick projects/inspections to move between environments. Same god-account signs into both preview + prod god panels — export from one, upload the JSON in the other.
      </p>

      {/* Kind tabs */}
      <div className="mt-4 flex gap-1 border-b-2 border-slate-200 overflow-x-auto">
        {[
          { id: "inspection_projects", label: "Projects (ProjectRadar)" },
          { id: "tenant_inspections", label: "Inspections (TenantRadar)" },
          { id: "landlords", label: "Landlords + Properties" },
          { id: "assets", label: "Assets (Washers, Alarms, HVAC)" },
        ].map(t => (
          <button key={t.id} onClick={() => setKind(t.id)}
            data-testid={`migration-tab-${t.id}`}
            className={`px-4 h-10 whitespace-nowrap text-xs font-black uppercase tracking-widest border-b-4 -mb-0.5 ${kind === t.id ? "border-fuchsia-600 text-fuchsia-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Export column */}
        <div className="p-4 bg-white border-2 border-[#0f172a]">
          <div className="font-black uppercase text-sm tracking-widest">1. Export FROM</div>
          <select value={sourceTid} onChange={e => setSourceTid(e.target.value)}
            data-testid="migration-source-select"
            className="mt-2 w-full h-10 px-2 border-2 border-[#0f172a] font-bold text-sm">
            <option value="">— pick source company —</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          {sourceTid && (
            <>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {listBusy ? "loading…" : `${rows.length} item(s) · ${selected.size} selected · ~${estMB} MB`}
                </div>
                <button onClick={toggleAll} disabled={rows.length === 0}
                  data-testid="migration-toggle-all"
                  className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:underline disabled:opacity-40">
                  {selected.size === rows.length && rows.length > 0 ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto border-2 border-slate-200" data-testid="migration-list">
                {rows.length === 0 && !listBusy && (
                  <div className="p-3 text-xs text-slate-400 font-medium">No items in this tenant.</div>
                )}
                {rows.map(r => {
                  const checked = selected.has(r.id);
                  const label = kind === "inspection_projects" ? r.name
                    : kind === "tenant_inspections" ? (r.template_name || "Inspection")
                    : r.name;
                  const sub = kind === "landlords"
                    ? `${r.email || "no email"} · ${r.property_count || 0} propert${r.property_count === 1 ? "y" : "ies"}`
                    : kind === "assets"
                    ? `${r.asset_count || 0} asset${r.asset_count === 1 ? "" : "s"}${r.landlord_id ? " · linked" : ""}`
                    : `${r.property_address || ""}${r.unit ? ` · Unit ${r.unit}` : ""}${r.status ? ` · ${r.status}` : ""} · ${r.photo_count} photo${r.photo_count === 1 ? "" : "s"}`;
                  return (
                    <label key={r.id} className="flex items-start gap-2 p-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`migration-item-${r.id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(r.id)}
                        data-testid={`migration-check-${r.id}`}
                        className="mt-0.5 h-4 w-4 accent-fuchsia-600"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{label}</div>
                        <div className="text-[10px] text-slate-500 truncate">{sub}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {hasPhotos && (
                <div className="mt-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Photo quality</div>
                  <div className="flex gap-1">
                    {[
                      { id: "small", label: "Small (1600px)", sub: "~450KB/photo" },
                      { id: "full",  label: "Full quality",   sub: "~4.5MB/photo" },
                    ].map(o => (
                      <button key={o.id} onClick={() => setPhotoQuality(o.id)}
                        data-testid={`migration-quality-${o.id}`}
                        className={`flex-1 p-2 border-2 text-[10px] font-black uppercase tracking-widest ${photoQuality === o.id ? "border-fuchsia-600 bg-fuchsia-50 text-fuchsia-700" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}>
                        <div>{o.label}</div>
                        <div className="text-[9px] font-medium tracking-normal normal-case mt-0.5">{o.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={doExport} disabled={exportBusy || selected.size === 0}
                data-testid="migration-export-btn"
                className="mt-3 w-full h-10 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest">
                {exportBusy ? "Exporting… (may take 20-60s)" : `⬇ Download JSON (${selected.size})`}
              </button>
            </>
          )}
        </div>

        {/* Import column */}
        <div className="p-4 bg-white border-2 border-[#0f172a]">
          <div className="font-black uppercase text-sm tracking-widest">2. Import INTO</div>
          <select value={targetTid} onChange={e => setTargetTid(e.target.value)}
            data-testid="migration-target-select"
            className="mt-2 w-full h-10 px-2 border-2 border-[#0f172a] font-bold text-sm">
            <option value="">— pick target company —</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className={`mt-3 w-full h-10 flex items-center justify-center text-xs font-black uppercase tracking-widest cursor-pointer ${importBusy || !targetTid ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
            {importBusy ? "Importing…" : "⬆ Upload JSON to Restore"}
            <input type="file" accept=".json,application/json,text/plain,text/*,*/*" onChange={doImport}
              disabled={importBusy || !targetTid} data-testid="migration-import-input" className="hidden"/>
          </label>
          <p className="mt-2 text-[10px] text-slate-500 font-medium">Duplicates (same name / same address+unit) are skipped automatically. Schema auto-detected from the JSON file.</p>
        </div>
      </div>

      {importSummary && (
        <div className="mt-3 p-3 border-2 border-emerald-600 bg-emerald-50 text-sm" data-testid="migration-summary">
          <b>Import complete</b> — imported <b>{importSummary.imported}</b>, skipped <b>{importSummary.skipped_existing}</b>, migrated <b>{importSummary.photos_migrated}</b> photo(s).
        </div>
      )}

      <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 p-2">
        ⚠️ <b>Workflow</b>: Sign into <i>this god panel</i> on the SOURCE environment (e.g. preview) → export → download JSON. Then sign into the god panel on the TARGET environment (e.g. fieldradar.pro) → upload the same JSON.
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="rugged-card p-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 bg-[#0f172a] text-white flex items-center justify-center">{icon}</div>
        <div className="min-w-0">
          <div className="text-xs font-black uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-display font-black text-3xl leading-none mt-1">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1 font-medium">{sub}</div>}
        </div>
      </div>
    </div>
  );
}
